import React, { useState, useEffect, useCallback, useRef } from 'react'
import { Right } from '../components/List'
import Icon from '../components/Icon'
import Potency from '../components/ThreePhase/Potency'
import { useAppDispatch, useAppSelector } from '../hooks'
import {
  View,
  TouchableOpacity,
  Text,
  StyleSheet, Platform, SafeAreaView, ScrollView
} from 'react-native'
import moment from 'moment'
import TitleLoading from '../components/TitleLoading'
import { ECharts } from '../components/ECharts'
import { HeaderButton, HeaderButtons, Item } from 'react-navigation-header-buttons'
import Utils from '../redux/utils'
import BlurView from '../components/BlurView'
import { t, gettext } from 'ttag'
import MyioDatePicker from '../components/MyioDatePicker'
import { useAppTheme } from '#app/theme'
import { useNavigation } from '@react-navigation/native'
import SegmentedControl from '@react-native-segmented-control/segmented-control'
import ConsumptionActions from '../redux/consumption'

const IoniconsHeaderButton = (props: any) => {
  return (
    <HeaderButton
      {...props}
      IconComponent={Icon}
      iconSize={23}
      color={props.color}
    />
  )
}

interface PulseEntry {
  timestamp: Date
  value: number
  delta: number
}

export default function FlowHistory () {
  const dispatch = useAppDispatch()
  const [selectedTab, setSelectedTab] = useState(0)

  const [period] = useState([{ label: '24h', value: 'hour' }, { label: gettext('Month'), value: 'day' }, { label: gettext('Year'), value: 'month' }])
  const [selectedPeriod, setSelectedPeriod] = useState({ label: '24h', value: 'hour' })

  const navigation = useNavigation()
  const theme = useAppTheme()

  const { flowSensor } = useAppSelector(state => state.consumption)

  const liveDevice = useAppSelector(state =>
    (state.slaves.allSlaves as any[])?.find(
      (d: any) => d.type === 'flow_sensor' &&
        d.slave_id === flowSensor.selectedDevice.slave_id &&
        d.channel === flowSensor.selectedDevice.channel
    )
  )

  const [pulseLog, setPulseLog] = useState<PulseEntry[]>([])
  const prevValueRef = useRef<number | null>(null)
  const scrollViewRef = useRef<ScrollView>(null)

  useEffect(() => {
    if (liveDevice?.value == null) return
    const curr = liveDevice.value as number
    if (prevValueRef.current !== null && curr !== prevValueRef.current) {
      const delta = curr - prevValueRef.current
      setPulseLog(log => [...log, { timestamp: new Date(), value: curr, delta }])
      scrollViewRef.current?.scrollToEnd({ animated: true })
    }
    prevValueRef.current = curr
  }, [liveDevice?.value])

  const years = Utils.getYearList()

  const months = [
    { label: gettext('JANUARY'), value: '01' },
    { label: gettext('FEBRUARY'), value: '02' },
    { label: gettext('MARCH'), value: '03' },
    { label: gettext('APRIL'), value: '04' },
    { label: gettext('MAY'), value: '05' },
    { label: gettext('JUNE'), value: '06' },
    { label: gettext('JULY'), value: '07' },
    { label: gettext('AUGUST'), value: '08' },
    { label: gettext('SEPTEMBER'), value: '09' },
    { label: gettext('OCTOBER'), value: '10' },
    { label: gettext('NOVEMBER'), value: '11' },
    { label: gettext('DECEMBER'), value: '12' }
  ]

  const [showPickerDate, setShowPickerDate] = useState(false)
  const [date, setDate] = useState(moment().set({ hour: 0, minutes: 0, seconds: 0 }).toDate())
  const [month, setMonth] = useState(months.find(m => m.value === moment().format('MM')))
  const [year, setYear] = useState(years.find(m => m.value === moment().format('YYYY')))

  useEffect(() => {
    dispatch(ConsumptionActions.resetFlowSensor())
    return () => {
      dispatch(ConsumptionActions.clearFlowSensor())
    }
  }, // eslint-disable-next-line react-hooks/exhaustive-deps
  [])

  useEffect(() => {
    if (selectedTab === 1) updateData()
  }, // eslint-disable-next-line react-hooks/exhaustive-deps
  [date, month, year, selectedPeriod, selectedTab, dispatch])

  const updateData = useCallback(() => {
    let payload = {}

    if (selectedPeriod.value === 'hour') {
      payload = { period: 'hour', date: moment(date, 'DD/MM/YYYY').toDate() }
    } else if (selectedPeriod.value === 'day') {
      // @ts-expect-error ts-migrate(2532) FIXME: Object is possibly 'undefined'.
      payload = { period: 'day', month: month.value, year: year.value }
    } else if (selectedPeriod.value === 'month') {
      // @ts-expect-error ts-migrate(2532) FIXME: Object is possibly 'undefined'.
      payload = { period: 'month', year: year.value }
    }

    if (moment(date, 'DD/MM/YYYY', true).isValid()) {
      dispatch({ type: 'GET_FLOW_CONSUMPTION_REQUEST', payload })
    }
  }, [date, month, year, selectedPeriod, dispatch])

  useEffect(() => {
    navigation.setOptions({
      title: flowSensor.selectedDevice.name,
      headerRight: () => {
        return (
          <HeaderButtons HeaderButtonComponent={IoniconsHeaderButton}>
            <Item
              title={t`Refresh`}
              iconName="refresh"
              onPress={() => {
                dispatch(ConsumptionActions.resetFlowSensor())
                if (selectedTab === 1) updateData()
              }}
            />
          </HeaderButtons>
        )
      }
    })
  },
  [navigation, dispatch, updateData, flowSensor, selectedTab])

  const getDate = () => {
    return moment(date).startOf('day').tz('America/Sao_Paulo').format('DD/MM/YYYY').toString()
  }

  const getChartDf = () => {
    return flowSensor.consumptionData.values.map((c: any) => {
      return {
        value: [c.label, parseFloat(c.value)]
      }
    })
  }

  const getChartTooltipFormatter = () => {
    let formatter = (params: any) => {
      const d = new Date(params[0].value[0])
      const value = parseFloat(params[0].value[1]).toFixed(2)
      // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
      const dt = ('0' + d.getHours()).substr(-2) + ':' + ('0' + d.getMinutes()).substr(-2)
      return `${dt}</br>${value} pulsos`
    }

    if (selectedPeriod.value === 'day') {
      formatter = (params) => {
        const d = new Date(params[0].value[0])
        const value = parseFloat(params[0].value[1]).toFixed(2)
        // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
        const dt = ('0' + d.getUTCDate()).substr(-2) + '/' + ('0' + (d.getUTCMonth() + 1)).substr(-2) + '/' + ('0' + d.getUTCFullYear()).substr(-2)
        return `${dt}</br>${value} pulsos`
      }
    } else if (selectedPeriod.value === 'month') {
      formatter = (params) => {
        const d = new Date(params[0].value[0])
        const value = parseFloat(params[0].value[1]).toFixed(2)
        // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
        const dt = ('0' + (d.getUTCMonth() + 1)).substr(-2) + '/' + ('0' + d.getUTCFullYear()).substr(-2)
        return `${dt}</br>${value} pulsos`
      }
    }

    return formatter
  }

  const getChartAxisLabelFormatter = () => {
    let formatter = (value: any, index: any) => {
      const dt = new Date(value)
      // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
      return ('0' + dt.getHours()).substr(-2) + 'H'
    }

    if (selectedPeriod.value === 'day') {
      // @ts-expect-error ts-migrate(2322) FIXME: Type '(value: any, index: any) => number' is not a...
      formatter = (value, index) => {
        const dt = new Date(value)
        return dt.getUTCDate()
      }
    } else if (selectedPeriod.value === 'month') {
      formatter = (value, index) => {
        const dt = new Date(value)
        // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
        return ('0' + dt.getUTCMonth()).substr(-2)
      }
    }

    return formatter
  }

  const getChartOptions = () => {
    return {
      xAxis: {
        show: true,
        type: 'category',
        maxInterval: 3600 * 1000 * 24,
        axisLine: {
          lineStyle: { color: theme.textColor }
        },
        axisLabel: {
          formatter: getChartAxisLabelFormatter()
        }
      },
      yAxis: {
        show: true,
        type: 'value',
        axisLine: {
          lineStyle: { color: theme.textColor }
        },
        splitLine: { show: false }
      },
      series: [{
        data: getChartDf(),
        type: 'bar',
        itemStyle: { normal: { color: theme.colors.default } }
      }],
      grid: { left: 25, top: 15, right: 25, bottom: 25 },
      tooltip: {
        show: true,
        trigger: 'axis',
        formatter: getChartTooltipFormatter(),
        axisPointer: { type: 'none' }
      }
    }
  }

  const renderDatePicker = () => {
    if (selectedPeriod.value === 'hour') {
      if (Platform.OS !== 'web') {
        return (
          <View style={styles.flexPicker}>
            <TouchableOpacity
              onPress={() => { setShowPickerDate(!showPickerDate) }}
              style={[theme.form.inputTouchable, theme.border]}
            >
              <Text style={{ fontSize: 15, color: theme.textColor }}>{getDate()}</Text>
              <Right>
                <Icon name="chevron-down-circle-outline" size={20} color={theme.colors.grey} />
              </Right>
            </TouchableOpacity>

            {showPickerDate && (
              <MyioDatePicker date={date} theme={theme} onDateChange={setDate} />
            )}
          </View>
        )
      } else {
        return (
          <View style={styles.flexPicker}>
            <MyioDatePicker date={date} theme={theme} onDateChange={setDate} />
          </View>
        )
      }
    }
  }

  const renderLiveTab = () => {
    return (
      <ScrollView
        ref={scrollViewRef}
        style={{ flex: 1 }}
        contentContainerStyle={{ padding: 12 }}
      >
        {pulseLog.length === 0
          ? (
            <Text style={{ color: theme.colors.grey, textAlign: 'center', marginTop: 20 }}>
              {t`Waiting for flow events...`}
            </Text>
            )
          : pulseLog.map((entry, i) => (
            <View key={i} style={[styles.pulseRow, { borderBottomColor: theme.colors.grey }]}>
              <Text style={{ color: theme.colors.grey, fontSize: 12 }}>
                {moment(entry.timestamp).format('HH:mm:ss')}
              </Text>
              <Text style={{ color: theme.textColor, fontSize: 15, fontWeight: '600' }}>
                {`+${entry.delta} pulsos`}
              </Text>
              <Text style={{ color: theme.colors.grey, fontSize: 12 }}>
                {`${entry.value} pulsos total`}
              </Text>
            </View>
          ))}
      </ScrollView>
    )
  }

  const renderHistoryTab = () => {
    return (
      <View style={{ flex: 1 }}>
        <View style={[theme.form.formGroup, theme.pb0, { marginBottom: 0, flexDirection: 'row' }]}>
          <TouchableOpacity
            onPress={() => {
              navigation.navigate('ModalPicker', {
                name: gettext('Period'),
                data: period,
                value: selectedPeriod,
                search: true,
                multiple: false,
                required: true,
                onPressOk: (data: any) => {
                  setSelectedPeriod(data.selectedItem)
                }
              })
            }}
            style={[theme.form.inputTouchable, styles.flexPicker, theme.border]}
          >
            <Text style={{ fontSize: 15, color: theme.textColor }}>{selectedPeriod.label}</Text>
            <Right>
              <Icon name="chevron-down-circle-outline" size={20} color={theme.colors.grey} />
            </Right>
          </TouchableOpacity>
        </View>

        <View style={[theme.form.formGroup, theme.pb0, { marginBottom: 0, flexDirection: 'row' }]}>
          {renderDatePicker()}

          {selectedPeriod.value === 'day' &&
            <View style={[styles.flexPicker, {
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'center',
              alignItems: 'center'
            }]}
            >
              <TouchableOpacity
                onPress={() => {
                  navigation.navigate('ModalPicker', {
                    name: gettext('Select an month...'),
                    data: months,
                    value: month,
                    search: true,
                    multiple: false,
                    required: true,
                    onPressOk: (data: any) => {
                      setMonth(data.selectedItem)
                    }
                  })
                }}
                style={[theme.form.inputTouchable, theme.border, {
                  justifyContent: 'center',
                  width: '50%',
                  borderTopEndRadius: 0,
                  borderBottomEndRadius: 0,
                  borderRightWidth: 0
                }]}
              >
                {/* @ts-expect-error ts-migrate(2532) FIXME: Object is possibly 'undefined'. */}
                <Text style={{ fontSize: 15, color: theme.textColor }}>{month.label}</Text>
                <Right>
                  <Icon name="chevron-down-circle-outline" size={20} color={theme.colors.grey} />
                </Right>
              </TouchableOpacity>

              <TouchableOpacity
                onPress={() => {
                  navigation.navigate('ModalPicker', {
                    name: gettext('Select a year...'),
                    data: years,
                    value: year,
                    search: true,
                    multiple: false,
                    required: true,
                    onPressOk: (data: any) => {
                      setYear(data.selectedItem)
                    }
                  })
                }}
                style={[theme.form.inputTouchable, theme.border, {
                  justifyContent: 'center',
                  width: '50%',
                  borderTopStartRadius: 0,
                  borderBottomStartRadius: 0,
                  borderLeftWidth: 0
                }]}
              >
                {/* @ts-expect-error ts-migrate(2532) FIXME: Object is possibly 'undefined'. */}
                <Text style={{ fontSize: 15, color: theme.textColor }}>{year.label}</Text>
                <Right>
                  <Icon name="chevron-down-circle-outline" size={20} color={theme.colors.grey} />
                </Right>
              </TouchableOpacity>
            </View>}

          {selectedPeriod.value === 'month' &&
            <TouchableOpacity
              onPress={() => {
                navigation.navigate('ModalPicker', {
                  name: gettext('Select a year...'),
                  data: years,
                  value: year,
                  search: true,
                  multiple: false,
                  required: true,
                  onPressOk: (data: any) => {
                    setYear(data.selectedItem)
                  }
                })
              }}
              style={[theme.form.inputTouchable, styles.flexPicker, theme.border]}
            >
              {/* @ts-expect-error ts-migrate(2532) FIXME: Object is possibly 'undefined'. */}
              <Text style={{ fontSize: 15, color: theme.textColor }}>{year.label}</Text>
              <Right>
                <Icon name="chevron-down-circle-outline" size={20} color={theme.colors.grey} />
              </Right>
            </TouchableOpacity>}
        </View>

        <View style={{ paddingHorizontal: 5, paddingTop: 4, flexDirection: 'row' }}>
          <Potency
            label={`${gettext('Flow of')} ${selectedPeriod.label}`}
            // @ts-expect-error ts-migrate(2322) FIXME: Type '{ label: string; loading: boolean; value: st...
            loading={flowSensor.loading}
            value={`${isNaN(flowSensor.consumptionData.totalValue) || flowSensor.consumptionData.totalValue == null ? '0.00' : flowSensor.consumptionData.totalValue.toFixed(2)} pulsos`}
          />
        </View>

        <View style={[theme.mb3, {
          flex: 1,
          flexDirection: 'column',
          alignItems: 'stretch',
          display: 'flex',
          padding: 5
        }]}
        >
          <BlurView
            tint={theme.blurView.tint} intensity={theme.blurView.intensity}
            style={[theme.border, theme.p3, { height: '100%' }]}
          >
            {/* @ts-expect-error ts-migrate(2322) FIXME: Type 'string' is not assignable to type '(string &... Remove this comment to see the full error message */}
            <TitleLoading loading={flowSensor.loading} text={t`Flow until now`} style={{ flex: 1 }} />
            {flowSensor?.consumptionData?.values != null && (
              <View style={{ flex: 2 }}>
                <ECharts
                  // @ts-expect-error ts-migrate(2322) FIXME: Type '{ option: { xAxis: { show: boolean; type: st...
                  option={getChartOptions()}
                  backgroundColor="transparent"
                />
              </View>
            )}
          </BlurView>
        </View>
      </View>
    )
  }

  return (
    <SafeAreaView style={{ flex: 1 }}>
      <View style={{ paddingHorizontal: 12, paddingVertical: 10 }}>
        <SegmentedControl
          values={[t`Live`, t`History`]}
          selectedIndex={selectedTab}
          onChange={(event) => setSelectedTab(event.nativeEvent.selectedSegmentIndex)}
        />
      </View>

      {selectedTab === 0 ? renderLiveTab() : renderHistoryTab()}
    </SafeAreaView>
  )
}

const styles = StyleSheet.create({
  flexPicker: {
    flexGrow: 1,
    flexShrink: 1,
    flexBasis: 0,
    margin: 5
  },
  pulseRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 10,
    borderBottomWidth: StyleSheet.hairlineWidth
  }
})
